import { useTranslation } from 'react-i18next';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import {
  DREAMCOAT_PAGE_TITLES,
  DreamcoatPageTitleKey,
} from './dreamcoatPageTitles';

/**
 * Returns true when the Dreamcoat Frame feature flag is enabled for the
 * current session. Useful for the small number of Dreamcoat-gated bits that
 * aren't page titles (e.g. the New Availability back-link label).
 */
export const useDreamcoatFlag = (): boolean => {
  const { session } = useOptionalSession();
  return !!session && hasFeatureFlag(session, FeatureFlag.DreamcoatFrame);
};

/**
 * Centralized resolver for the Dreamcoat-only page title overrides.
 *
 * When the Dreamcoat Frame flag is OFF (or no session is available yet) the
 * page's existing `legacyTitle` is returned verbatim, preserving today's
 * behavior. When the flag is ON, the translated value from
 * `DREAMCOAT_PAGE_TITLES` is returned (with optional interpolation params).
 *
 * The returned string can flow into both `<Meta>` (browser tab) and
 * `AppFrameConfig`/`PageHeader` (in-frame heading) so a single hook call
 * keeps the two in sync.
 */
export function useDreamcoatPageTitle(
  key: DreamcoatPageTitleKey,
  legacyTitle: string,
  params?: Record<string, string | number>
): string;
export function useDreamcoatPageTitle(
  key: DreamcoatPageTitleKey,
  legacyTitle: string | undefined,
  params?: Record<string, string | number>
): string | undefined;
export function useDreamcoatPageTitle(
  key: DreamcoatPageTitleKey,
  legacyTitle: string | undefined,
  params?: Record<string, string | number>
): string | undefined {
  const { t } = useTranslation();
  const dreamcoatOn = useDreamcoatFlag();

  if (!dreamcoatOn) {
    return legacyTitle;
  }

  return t(DREAMCOAT_PAGE_TITLES[key], params ?? {});
}
